import type { ApiClient } from "./http";
import type {
  AppleMusicConnection,
  BillingSnapshot,
  Playlist,
  RadioRun,
  RadioRunEventPage,
  RadioStartAdmission,
  RadioStartJob,
  RadioStartRequest,
  UserProfile,
  VoiceCatalog,
} from "./types";

/** Every backend call the web player makes, typed. */
export class Backend {
  constructor(readonly api: ApiClient) {}

  me(): Promise<UserProfile> {
    return this.api.request("/api/users/me");
  }

  billing(): Promise<BillingSnapshot> {
    return this.api.request("/api/billing/status");
  }

  voices(): Promise<VoiceCatalog> {
    return this.api.request("/api/ai/voice-profiles?page_size=50");
  }

  admitRadioStart(voiceProfileID: string): Promise<RadioStartAdmission> {
    return this.api.request("/api/ai/radio-start-admission", {
      method: "POST",
      body: { voice_profile_id: voiceProfileID, uses_visual_context: false },
    });
  }

  radioCapabilities(): Promise<{ durable_start_jobs: boolean }> {
    return this.api.request("/api/radio/capabilities");
  }

  startRadio(request: RadioStartRequest): Promise<RadioStartJob> {
    return this.api.request("/api/radio/start-jobs", { method: "POST", body: request });
  }

  startJob(jobID: string): Promise<RadioStartJob> {
    return this.api.request(`/api/radio/start-jobs/${jobID}`);
  }

  cancelStartJob(jobID: string): Promise<RadioStartJob> {
    return this.api.request(`/api/radio/start-jobs/${jobID}`, { method: "DELETE" });
  }

  run(playlistID: string): Promise<RadioRun> {
    return this.api.request(`/api/radio/runs/${playlistID}`);
  }

  runEvents(playlistID: string, after: number): Promise<RadioRunEventPage> {
    return this.api.request(`/api/radio/runs/${playlistID}/events?after=${after}`);
  }

  reportCursor(playlistID: string, position: number, offsetSeconds: number): Promise<unknown> {
    return this.api.request(`/api/radio/runs/${playlistID}/cursor`, {
      method: "POST",
      body: { position, offset_seconds: Math.max(0, offsetSeconds) },
    });
  }

  setDirectives(playlistID: string, directives: string[], flushTail: boolean): Promise<unknown> {
    return this.api.request(`/api/radio/runs/${playlistID}/directives`, {
      method: "POST",
      body: { directives, flush_tail: flushTail },
    });
  }

  reportUnplayable(playlistID: string, position: number, rejectedIDs: string[]): Promise<unknown> {
    return this.api.request(`/api/radio/runs/${playlistID}/unplayable`, {
      method: "POST",
      body: { position, rejected_ids: rejectedIDs.slice(0, 20) },
    });
  }

  pauseRun(playlistID: string): Promise<RadioRun> {
    return this.api.request(`/api/radio/runs/${playlistID}/pause`, { method: "POST" });
  }

  resumeRun(playlistID: string): Promise<RadioRun> {
    return this.api.request(`/api/radio/runs/${playlistID}/resume`, { method: "POST" });
  }

  stopRun(playlistID: string): Promise<RadioRun> {
    return this.api.request(`/api/radio/runs/${playlistID}/stop`, { method: "POST" });
  }

  playlists(): Promise<Playlist[]> {
    return this.api.request("/api/playlists?limit=50&archive=active");
  }

  playlist(playlistID: string): Promise<Playlist> {
    return this.api.request(`/api/playlists/${playlistID}`);
  }

  appleMusicDeveloperToken(): Promise<{ token: string }> {
    return this.api.request("/api/music/apple-music/developer-token");
  }

  appleMusicConnection(): Promise<AppleMusicConnection> {
    return this.api.request("/api/music/apple-music/connection");
  }

  /** Lets the server read the listener's library while the producer plans. */
  storeAppleMusicUserToken(token: string): Promise<AppleMusicConnection> {
    return this.api.request("/api/music/apple-music/user-token", {
      method: "PUT",
      body: { token },
    });
  }

  soundCloudStream(urn: string): Promise<{ stream_url: string }> {
    return this.api.request(`/api/music/soundcloud/tracks/${encodeURIComponent(urn)}/stream`);
  }
}
